// Payment-method marks for the GCC cashier redesign. Rendering priority is:
// (1) the real back-office logo when the engine returns an uploaded `logo` URL,
// else (2) a curated inline-SVG icon when the method name/label matches a known
// keyword, else (3) legible initials. The engine returns a shared placeholder
// (filename contains "default-payment-method-icon") when a method has no uploaded
// logo; that placeholder is treated as no-logo so the curated icon wins instead.
import { useState } from "react";

function methodInitials(str) {
  const s = String(str || "").trim();
  if (!s) return "?";
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

const FALLBACK_STYLE = {
  fontFamily: "Manrope, sans-serif",
  fontWeight: 800,
  fontSize: "14px",
  letterSpacing: "-.2px",
  // Light ink: the logo box is transparent on the dark cashier panel now.
  color: "#e9fbf3",
};

// Colored coin disc with a ticker glyph, for coins without a bespoke logo path.
function coinCircle(fill, ticker, fontSize = 8.5) {
  return (
    <svg width="33" height="33" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="16" fill={fill} />
      <text
        x="16"
        y="20"
        textAnchor="middle"
        fontFamily="Manrope,sans-serif"
        fontSize={fontSize}
        fontWeight="800"
        fill="#fff"
      >
        {ticker}
      </text>
    </svg>
  );
}

// Simple wordmark chip on a brand-colored .logo box.
function wordmark(text, fill, fontSize = 10) {
  return (
    <svg width="42" height="12" viewBox="0 0 54 14">
      <text
        x="27"
        y="11"
        textAnchor="middle"
        fontFamily="Manrope,sans-serif"
        fontSize={fontSize}
        fontWeight="800"
        fill={fill}
        letterSpacing="-.2"
      >
        {text}
      </text>
    </svg>
  );
}

// Ordered keyword -> mark table. Match is by squished, accent-folded keyword so
// "Tether (Erc-20)", "TRON (TRX)" and method_ids like "bank_transfer" all
// resolve. Order matters: compound/ticker keys that are substrings of another
// brand (e.g. "eth" inside "tether") come first so the right icon wins.
const CURATED = [
  {
    keys: ["tether", "usdt"],
    cls: "coin",
    node: (
      <svg width="33" height="33" viewBox="0 0 32 32">
        <circle cx="16" cy="16" r="16" fill="#26A17B" />
        <path d="M17.9 13.9v-2.4h5.4V7.9H8.7v3.6h5.4v2.4c-4.4.2-7.7 1.1-7.7 2.1s3.3 1.9 7.7 2.1v7h3.8v-7c4.4-.2 7.6-1.1 7.6-2.1s-3.2-1.9-7.6-2.1Zm0 3.6c-.1 0-.7.1-1.9.1-1 0-1.6 0-1.9-.1-3.9-.2-6.7-.8-6.7-1.6s2.9-1.4 6.7-1.6v2.6c.3 0 1 .1 1.9.1 1.1 0 1.7-.1 1.9-.1v-2.6c3.8.2 6.7.8 6.7 1.6s-2.9 1.4-6.7 1.6Z" fill="#fff" />
      </svg>
    ),
  },
  {
    keys: ["bitcoin", "btc"],
    cls: "coin",
    node: (
      <svg width="33" height="33" viewBox="0 0 32 32">
        <circle cx="16" cy="16" r="16" fill="#F7931A" />
        <path d="M22.2 14c.3-2-1.2-3-3.3-3.8l.7-2.7-1.6-.4-.7 2.6c-.4-.1-.9-.2-1.3-.3l.7-2.6-1.6-.4-.7 2.7c-.3-.1-.7-.2-1-.3l-2.3-.6-.4 1.7s1.2.3 1.2.3c.7.2.8.6.8 1l-.8 3.1v.1l-1.1 4.4c-.1.2-.3.5-.8.4 0 0-1.2-.3-1.2-.3l-.8 1.9 2.1.5c.4.1.8.2 1.2.3l-.7 2.7 1.6.4.7-2.7c.4.1.9.2 1.3.3l-.7 2.7 1.6.4.7-2.7c2.8.5 4.9.3 5.8-2.2.7-2-.1-3.2-1.5-4 1.1-.2 1.9-1 2.1-2.4Zm-3.7 5.2c-.5 2-3.9.9-5 .7l.9-3.6c1.1.3 4.7.8 4.1 2.9Zm.5-5.3c-.5 1.9-3.3.9-4.2.7l.8-3.3c.9.2 3.9.7 3.4 2.6Z" fill="#fff" />
      </svg>
    ),
  },
  {
    keys: ["ethereum", "eth"],
    cls: "coin",
    node: (
      <svg width="33" height="33" viewBox="0 0 32 32">
        <circle cx="16" cy="16" r="16" fill="#627EEA" />
        <path d="M16 5v8.1l6.9 3.1L16 5Z" fill="#fff" fillOpacity=".6" />
        <path d="M16 5 9.1 16.2 16 13.1V5Z" fill="#fff" />
        <path d="M16 21.4V27l6.9-9.6L16 21.4Z" fill="#fff" fillOpacity=".6" />
        <path d="M16 27v-5.6l-6.9-4L16 27Z" fill="#fff" />
      </svg>
    ),
  },
  {
    keys: ["litecoin", "ltc"],
    cls: "coin",
    node: (
      <svg width="33" height="33" viewBox="0 0 32 32">
        <circle cx="16" cy="16" r="16" fill="#345D9D" />
        <path d="m10.4 21.5.8-3.2-1.9.7.5-2.1 2-.7L13.4 9h3.6l-1.2 5.1 1.9-.7-.5 2-1.9.7-.8 3.2h7l-.6 2.7H10.4Z" fill="#fff" />
      </svg>
    ),
  },
  { keys: ["dogecoin", "doge"], cls: "coin", node: coinCircle("#C2A633", "DOGE", 7.5) },
  { keys: ["tron", "trx"], cls: "coin", node: coinCircle("#EF0027", "TRX") },
  { keys: ["xrp", "ripple"], cls: "coin", node: coinCircle("#23292F", "XRP") },
  // Fiat marks render transparent (no plate) on the dark cashier panel, so fills
  // are light/legible (white or a light brand tint), matching the clean crypto look.
  {
    keys: ["interac"],
    node: (
      <svg width="44" height="13" viewBox="0 0 46 14">
        <text x="23" y="11" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="10.5" fontWeight="800" fontStyle="italic" fill="#FDB913" letterSpacing="-.2">INTERAC</text>
      </svg>
    ),
  },
  {
    keys: ["openbanking"],
    node: (
      <svg width="31" height="31" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="8.5" stroke="#7ff2c7" strokeWidth="1.7" />
        <path d="M12 7v5l3.5 2.5" stroke="#7ff2c7" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    keys: ["banktransfer"],
    node: (
      <svg width="31" height="31" viewBox="0 0 24 24" fill="none">
        <path d="M3 9l9-5 9 5M4 9v9m4-9v9m4-9v9m4-9v9M3 20h18" stroke="#7ff2c7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  { keys: ["payid"], node: wordmark("PayID", "#fff", 12) },
  { keys: ["mybank"], node: wordmark("MyBank", "#ff5c78", 11) },
  { keys: ["multibanco"], node: wordmark("Multibanco", "#fff", 8.5) },
  { keys: ["rapidtransfer"], node: wordmark("Rapid", "#0EE06A", 12) },
  // Card tile: a Visa + Mastercard combo mark. Must precede the "lamecanica"
  // entry so a card method routed through La Mecanica (label "Credit / Debit
  // Card") shows card art, not the PSP wordmark. Keys are the squished label,
  // not the PSP name, so only genuine card tiles match.
  {
    keys: ["creditdebitcard", "creditcard", "debitcard", "creditdebit", "bankcard"],
    node: (
      <svg width="52" height="17" viewBox="0 0 58 18">
        <text x="0" y="13" fontFamily="Manrope,sans-serif" fontSize="12" fontWeight="800" fontStyle="italic" fill="#fff" letterSpacing="-.4">VISA</text>
        <circle cx="41" cy="9" r="7" fill="#EB001B" />
        <circle cx="50" cy="9" r="7" fill="#F79E1B" />
        <path d="M45.5 3.6a7 7 0 0 1 0 10.8 7 7 0 0 1 0-10.8Z" fill="#FF5F00" />
      </svg>
    ),
  },
  { keys: ["lamecanica", "mecanica"], node: wordmark("Mecanica", "#fff", 8.5) },
  {
    keys: ["trustly"],
    node: (
      <svg width="46" height="14" viewBox="0 0 52 14">
        <text x="26" y="11" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="11" fontWeight="800" fill="#0EE06A" letterSpacing="-.3">Trustly</text>
      </svg>
    ),
  },
  {
    keys: ["ideal"],
    node: (
      <svg width="44" height="15" viewBox="0 0 50 16">
        <text x="25" y="12.5" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="12" fontWeight="800" fill="#ff5c9d">iDEAL</text>
      </svg>
    ),
  },
  {
    keys: ["skrill"],
    node: (
      <svg width="38" height="13" viewBox="0 0 40 14">
        <text x="20" y="11" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="11" fontWeight="800" fill="#d98cc4" letterSpacing="-.2">Skrill</text>
      </svg>
    ),
  },
  {
    keys: ["neteller"],
    node: (
      <svg width="46" height="11" viewBox="0 0 52 12">
        <text x="26" y="10" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="9.5" fontWeight="800" fill="#a4d65e" letterSpacing=".2">NETELLER</text>
      </svg>
    ),
  },
  {
    keys: ["mifinity"],
    node: (
      <svg width="34" height="14" viewBox="0 0 44 16">
        <path d="M10 8c0-2.4 2-4.4 4.4-4.4 1.6 0 3 .9 3.8 2.2C19 4.5 20.4 3.6 22 3.6c2.4 0 4.4 2 4.4 4.4s-2 4.4-4.4 4.4c-1.6 0-3-.9-3.8-2.2-.8 1.3-2.2 2.2-3.8 2.2C12 12.4 10 10.4 10 8Zm4.4-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm7.6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" fill="url(#mfg)" />
        <defs>
          <linearGradient id="mfg" x1="10" y1="3" x2="27" y2="13">
            <stop stopColor="#00A7E1" />
            <stop offset="1" stopColor="#2ED9C3" />
          </linearGradient>
        </defs>
      </svg>
    ),
  },
  {
    keys: ["jeton"],
    node: (
      <svg width="36" height="13" viewBox="0 0 40 14">
        <text x="20" y="11" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="11" fontWeight="800" fill="#ff8a3c" letterSpacing="-.2">Jeton</text>
      </svg>
    ),
  },
  { keys: ["mbway", "mbw"], node: wordmark("MB WAY", "#ff6a8a", 9.5) },
  {
    keys: ["visa"],
    node: (
      <svg width="38" height="14" viewBox="0 0 40 14">
        <text x="20" y="11.5" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="12.5" fontWeight="800" fontStyle="italic" fill="#fff" letterSpacing="-.4">VISA</text>
      </svg>
    ),
  },
  {
    keys: ["mastercard"],
    node: (
      <svg width="32" height="20" viewBox="0 0 32 20">
        <circle cx="12" cy="10" r="9" fill="#EB001B" />
        <circle cx="20" cy="10" r="9" fill="#F79E1B" />
        <path d="M16 3.2a9 9 0 0 1 0 13.6 9 9 0 0 1 0-13.6Z" fill="#FF5F00" />
      </svg>
    ),
  },
  {
    keys: ["applepay"],
    node: (
      <svg width="38" height="16" viewBox="0 0 42 18">
        <path d="M8.4 4.9c.5-.7.9-1.6.8-2.5-.8 0-1.7.5-2.3 1.2-.5.6-1 1.5-.8 2.4.9 0 1.8-.5 2.3-1.1Zm1.6 1.4c-1.3-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.3 2-1.4 2.4-.4 6 1 8 .7 1 1.5 2 2.5 2s1.3-.6 2.5-.6 1.5.6 2.6.6 1.7-1 2.3-1.9c.7-1 1-2 1-2.1-.1 0-2-.8-2-3-.1-1.9 1.5-2.8 1.6-2.9-.9-1.3-2.3-1.5-2.6-2.1Z" fill="#fff" />
        <text x="15" y="14" fontFamily="Manrope,-apple-system,sans-serif" fontSize="12" fontWeight="700" fill="#fff">Pay</text>
      </svg>
    ),
  },
  {
    keys: ["googlepay"],
    node: (
      <svg width="38" height="15" viewBox="0 0 44 16">
        <text x="8" y="12.5" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="13" fontWeight="700" fill="#6ba0ff">G</text>
        <text x="27" y="12.5" textAnchor="middle" fontFamily="Manrope,sans-serif" fontSize="12" fontWeight="600" fill="#e9fbf3">Pay</text>
      </svg>
    ),
  },
];

function squish(str) {
  return String(str || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function resolveCurated(name, label) {
  const matchIn = (hay) =>
    hay ? CURATED.find((entry) => entry.keys.some((k) => hay.includes(k))) || null : null;
  // Match the human label ("Visa", "Mastercard") BEFORE the method_id
  // ("LAMECANICA:VISA"). Every La Mecanica method_id carries the "lamecanica"
  // prefix, which would otherwise hit the generic PSP wordmark and hide the
  // scheme art — so the label, which names the actual scheme, wins first.
  return matchIn(squish(label)) || matchIn(squish(name));
}

// A usable back-office logo: a non-empty URL/path that is neither a known
// placeholder (shared default icon, generic PSP card, legacy stock art) nor a
// bare name-text string.
function isRealLogo(logo) {
  if (typeof logo !== "string") return false;
  const url = logo.trim();
  if (!url) return false;
  const lower = url.toLowerCase();
  if (lower.includes("default-payment-method-icon")) return false;
  if (lower.includes("la-mecanica.png")) return false;
  if (lower.includes("payment.jpg")) return false;
  if (lower.includes("default_payment_logo")) return false;
  return /^(https?:\/\/|\/\/|\/|data:)/i.test(url);
}

export default function MethodLogo({ name, label, logo }) {
  const [imgFailed, setImgFailed] = useState(false);

  // Curated transparent marks WIN over uploaded logos: the wanted cashier look is
  // background-free marks recolored for the dark panel, while the seeded/uploaded
  // SVGs are self-contained brand cards (own white/dark backdrop baked into the
  // file — authored for light admin surfaces) and the seeder also mis-assigns the
  // generic La Mecanica card to e-wallets like Neteller/MB WAY. Uploaded logos
  // remain the fallback for methods the curated set doesn't know.
  const curated = resolveCurated(name, label);
  if (curated) {
    const className = curated.cls ? `logo ${curated.cls}` : "logo";
    return (
      <div className={className} style={curated.style}>
        {curated.node}
      </div>
    );
  }

  if (isRealLogo(logo) && !imgFailed) {
    // Unknown method with a real uploaded logo: render it on the transparent
    // wrapper (.plate) and let the asset bring its own backdrop.
    return (
      <div className="logo plate">
        <img src={logo} alt={label || name || ""} onError={() => setImgFailed(true)} />
      </div>
    );
  }

  // Unknown method: legible light initials on the transparent (dark) box.
  return (
    <div className="logo">
      <span style={FALLBACK_STYLE}>{methodInitials(label || name)}</span>
    </div>
  );
}
